import React, { useEffect, useState, useReducer, useCallback, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-hot-toast';
import CustomerSummary from './CustomerSummary';
import SubscriptionsTable, { SubscriptionsTableSkeleton } from './SubscriptionsTable';
import ShoppingCart from './ShoppingCart';
import ProductGridHorizontal from './ProductGridHorizontal';
import WorkspaceGrid from './WorkspaceGrid';
import { useApi } from '../hooks/useApi';
import SearchableSelect from './SearchableSelect';
import { Joyride, STATUS } from 'react-joyride';
import { CAP } from '../utils/permissions';
import { usePermissions } from '../utils/PermissionContext';
const initialState = {
    customers: [],
    isLoading: true,
    selectedCustomer: null,
    detailedSubscriptions: [],
    isLoadingDetails: false,
    extraProducts: [],
    cartItems: [],
    workspaces: [],
    emailTemplates: []
};

function appReducer(state, action) {
    switch (action.type) {
        case 'SET_CUSTOMERS':
            return { ...state, customers: action.payload };
        case 'SET_IS_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_SELECTED_CUSTOMER':
            return { ...state, selectedCustomer: action.payload };
        case 'SAVE_CUSTOMER':
            return { ...state, selectedCustomer: action.payload };
        case 'SET_DETAILED_SUBSCRIPTIONS':
            return { ...state, detailedSubscriptions: action.payload };
        case 'SET_IS_LOADING_DETAILS':
            return { ...state, isLoadingDetails: action.payload };
        case 'SET_EXTRA_PRODUCTS':
            return { ...state, extraProducts: action.payload };
        case 'SET_WORKSPACE_PRODUCTS':
            return { ...state, workspaces: action.payload };
        case 'SET_EMAIL_TEMPLATES':
            return { ...state, emailTemplates: action.payload };
        case 'SET_CART_ITEMS':
            return { ...state, cartItems: action.payload };
        case 'ADD_TO_CART':
            const existingItem = state.cartItems.find(item => item.id === action.payload.id);
            const payload = action.payload;
            if (!state.selectedCustomer) return state;
            if (payload.type === "license" && (payload.planName === "ANNUAL_YEARLY_PAY" || payload.planName === "ANNUAL")) {
                let workspaceProduct = state.workspaces.find((p) => p.related_licenses_sku === payload.skuId);
                if (!workspaceProduct) {
                    workspaceProduct = state.extraProducts.find((p) => p.related_licenses_sku === payload.skuId);
                }
                if (workspaceProduct) {
                    payload.afasArticleCode = workspaceProduct.afas_artikelcode;
                }
                if (workspaceProduct || payload.fetchedYearlyPrice !== undefined) {

                    let unitPricePerYear = parseFloat(payload.fetchedYearlyPrice !== undefined ? payload.fetchedYearlyPrice : (workspaceProduct ? workspaceProduct.price : 0)) || 0;
                    // Only the pro-rating needs a renewal date, and Google omits it on
                    // some subscriptions. Requiring it to price at all left those
                    // unbuyable with "could not determine price".
                    let diffTime = null;
                    if (payload.renewalDate) {
                        const [day, month, year] = payload.renewalDate.split('/');
                        diffTime = new Date(year, month - 1, day) - new Date();
                    }

                    if (payload.isRenewal || diffTime === null || diffTime <= 0) {
                        payload.price = unitPricePerYear;
                        payload.yearlyPrice = unitPricePerYear;
                    } else {
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        const dailyRate = unitPricePerYear / 365;
                        const proRatedPrice = dailyRate * diffDays;
                        payload.price = parseFloat(proRatedPrice.toFixed(2));
                        payload.diffDays = diffDays;
                        payload.yearlyPrice = unitPricePerYear;
                        console.log(`Pro-rated calc: ${diffDays} days remaining * ${dailyRate.toFixed(4)}/day = ${payload.price}`);
                    }
                }
            }

            if (payload.price === undefined || payload.price === null || isNaN(payload.price)) {
                const errorMsg = action.t?.price_not_found || "Could not determine price for this item. Please try again.";
                toast.error(errorMsg);
                return state;
            }

            // Toasted here, not by the caller: the caller cannot see that the
            // price check above rejected the item, so it used to report success
            // over an empty cart alongside the error.
            if (!action.silent) {
                toast.success(`${payload.name} ${action.t?.added_to_cart_success || "has been added to your shopping cart."}`);
            }

            if (existingItem) {
                return {
                    ...state,
                    cartItems: state.cartItems.map(item =>
                        item.id === payload.id ? { ...item, quantity: item.quantity + payload.quantity } : item
                    ),
                };
            }
            return { ...state, cartItems: [...state.cartItems, action.payload] };
        case 'REMOVE_FROM_CART':
            return { ...state, cartItems: state.cartItems.filter(item => item.id !== action.payload) };
        case 'RESET_CART':
            return { ...state, cartItems: [] };
        case 'RESET_CUSTOMER_STATE':
            return {
                ...state,
                customers: [],
                selectedCustomer: null,
                detailedSubscriptions: [],
                cartItems: [],
            };
        default:
            return state;
    }
}

const stripePromises = {};
const getStripePromise = (administratie) => {
    let key;
    if (administratie === "Xebia Nederland B.V.") {
        key = process.env.REACT_APP_STRIPE_PUB_KEY_NL;
    } else if (administratie === "Xebia Workplace B.V.") {
        key = process.env.REACT_APP_STRIPE_PUB_KEY_WP;
    } else if (administratie === "Xebia Belgium B.V.") {
        key = process.env.REACT_APP_STRIPE_PUB_KEY_BE;
    } else {
        key = process.env.REACT_APP_STRIPE_PUB_KEY_CLOUD;
    }

    if (!key) return null;

    if (!stripePromises[key]) {
        stripePromises[key] = loadStripe(key);
    }
    return stripePromises[key];
};

const CheckoutFormContent = ({ handlePlaceOrder, cartItems, customer, onRemoveFromCart, isPlacingOrder, onPayPalSuccess, purchaseOrder, setPurchaseOrder, t, language, setPaymentAmount }) => {
    const stripe = useStripe();
    const elements = useElements();

    const onSubmit = (e) => {
        handlePlaceOrder(e, stripe, elements);
    };

    return (
        <form onSubmit={onSubmit}>
            <ShoppingCart
                cartItems={cartItems}
                customer={customer}
                onRemoveFromCart={onRemoveFromCart}
                isPlacingOrder={isPlacingOrder}
                onPayPalSuccess={onPayPalSuccess}
                purchaseOrder={purchaseOrder}
                setPurchaseOrder={setPurchaseOrder}
                t={t}
                language={language}
                onTotalChange={setPaymentAmount}
            />
        </form>
    );
};

const getStoredPendingOrder = (customerId) => {
    if (!customerId) return null;
    try {
        return sessionStorage.getItem(`pending_order_${customerId}`) || null;
    } catch {
        return null;
    }
};

const setStoredPendingOrder = (customerId, orderId) => {
    if (!customerId) return;
    try {
        if (orderId) {
            sessionStorage.setItem(`pending_order_${customerId}`, orderId);
        } else {
            sessionStorage.removeItem(`pending_order_${customerId}`);
        }
    } catch {}
};

const Dashboard = ({ authUser, t, language, onLanguageChange, handleLogout, googleIdToken, runTour, setRunTour }) => {
    // Helper to wait for a specified duration
    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const [state, dispatch] = useReducer(appReducer, initialState);
    const {
        customers,
        isLoading,
        selectedCustomer,
        detailedSubscriptions,
        isLoadingDetails,
        extraProducts,
        cartItems,
        emailTemplates,
        workspaces
    } = state;

    const [orderConfirmation, setOrderConfirmation] = useState(null);
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [isCustomerChanged, setIsCustomerChanged] = useState(false);
    const [purchaseOrder, setPurchaseOrder] = useState('');
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [pendingOrderId, setPendingOrderId] = useState(null);

    // Handle return from Stripe redirect (failure or success) on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectStatus = urlParams.get('redirect_status');

        if (redirectStatus === 'failed') {
            setOrderConfirmation({
                success: false,
                message: t.payment_failed_authentication || "The payment failed or was cancelled. Please try another card or try again."
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (redirectStatus === 'succeeded') {
            setOrderConfirmation({
                success: true,
                message: t.payment_success_invoice_email || "Payment successful. The invoice will be sent to your email."
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [t]);

    // Sync pendingOrderId with sessionStorage when customer changes
    useEffect(() => {
        const custId = selectedCustomer?.customerId;
        if (!custId) {
            setPendingOrderId(null);
            return;
        }

        const stored = getStoredPendingOrder(custId);
        if (stored) {
            setPendingOrderId(stored);
        }
    }, [selectedCustomer?.customerId]);

    // One capability per thing being gated, rather than a single isAdmin flag.
    // Read-only and User differ by exactly one of these: both browse every
    // customer and see the internal status, only User may buy. An `isAdmin`
    // boolean would have to pick one of them to be wrong about.
    const { can } = usePermissions();
    const canSelectAnyCustomer = can(CAP.SELECT_ANY_CUSTOMER);
    const canPurchase = can(CAP.PURCHASE);
    const canViewInternalProducts = can(CAP.VIEW_INTERNAL_PRODUCTS);
    const { apiRequest } = useApi(googleIdToken, handleLogout);

    useEffect(() => {
        const hasSeenTour = localStorage.getItem('hasSeenTour');
        if (!hasSeenTour && setRunTour) {
            setRunTour(true);
            localStorage.setItem('hasSeenTour', 'true');
        }
    }, [setRunTour]);

    const handleJoyrideCallback = (data) => {
        const { status } = data;
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            if (setRunTour) setRunTour(false);
        }
    };

    const tourSteps = [
        {
            target: '.tour-customer-selector',
            content: t.tour_customer_selector || 'Select a customer to view their dashboard.',
            disableBeacon: true,
        },
        {
            target: '.tour-customer-summary',
            content: t.tour_customer_summary || 'View the basic details of the selected customer here.',
        },
        {
            target: '.tour-subscriptions-table',
            content: t.tour_subscriptions_table || 'Manage active subscriptions, renew, or increase licenses.',
        },
        {
            target: '.tour-cart',
            content: t.tour_cart || 'Review items in your cart and complete your order.',
        },
        {
            target: '.tour-extra-products',
            content: t.tour_extra_products || 'Explore and add additional products to your cart.',
        }
    ];

    // --- DATA FETCHING LOGIC ---
    const getCustomersApiEndpoint = useCallback(() => {
        if (!authUser) return null;

        let customersAPI = "/api/customers";

        // The backend derives the real scope from the verified identity and
        // ignores ?domain= from anyone without selectAnyCustomer, so this is now
        // only a filter hint. Kept so unprivileged callers still get a narrowed
        // query rather than the whole customer list.
        //
        // There used to be a second branch pinning any dev.gcompany.nl caller
        // to ?domain=phoenix.xebiaworkplace.com. It was an `else if`, so it fired
        // even for a caller who *can* select any customer — which is why staff
        // on that domain saw exactly one customer in the dropdown. It also
        // cannot work any more: the backend ignores ?domain= from anyone
        // without selectAnyCustomer, so it granted the QA persona nothing
        // either. Removed rather than re-guarded.
        if (!canSelectAnyCustomer) {
            customersAPI = customersAPI + "?domain=" + authUser.hd;
        }

        return customersAPI;
    }, [authUser, canSelectAnyCustomer]);

    const customerOptions = useMemo(() => {
        return (customers || [])
            .filter(c => c.isActive !== false)
            .map(c => {
                const id = c.customerId || c.id || '';
                const domain = c.customerDomain || '';
                const name = c.customerName || c.name || '';
                let label = domain;
                if (!label) {
                    label = name ? (id ? `${name} [${id}]` : name) : id;
                } else if (name && name.toLowerCase() !== domain.toLowerCase()) {
                    label = `${domain} (${name})`;
                }
                return { value: id, label: label || id || 'Unknown' };
            });
    }, [customers]);

    // Helper to select the initial customer (restoring from localStorage if available)
    const selectInitialCustomer = useCallback((customersData) => {
        if (customersData.length === 0) return;
        let initialCustomer;
        const storedCustomer = localStorage.getItem('selectedCustomer');
        if (storedCustomer) {
            const parsed = JSON.parse(storedCustomer);
            const found = customersData.find(c => (c.customerId || c.id) === (parsed.customerId || parsed.id));
            // For anyone who can browse customers, don't auto-restore a
            // now-inactive one (they're hidden from the dropdown, so restoring
            // one is inconsistent). Callers pinned to their own customer always
            // load it below, even if inactive.
            if (found && (!canSelectAnyCustomer || found.isActive !== false)) {
                initialCustomer = found;
            }
        }
        if (!initialCustomer) {
            if (!canSelectAnyCustomer && authUser.hd === 'dev.gcompany.nl') {
                initialCustomer = customersData.find(customer => customer.customerId === "C0405e68g") || customersData[0];
            } else if (canSelectAnyCustomer) {
                // Match the dropdown: default to the first active customer.
                initialCustomer = customersData.find(c => c.isActive !== false) || customersData[0];
            } else {
                initialCustomer = customersData[0];
            }
        }
        dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: initialCustomer });
    }, [authUser, canSelectAnyCustomer]);

    // Effect for fetching the initial list of customers and products
    useEffect(() => {
        if (!authUser) return;
        const customersAPI = getCustomersApiEndpoint();
        if (!customersAPI) return;

        dispatch({ type: 'SET_IS_LOADING', payload: true });

        Promise.all([
            apiRequest(customersAPI),
            apiRequest('/api/product-catalog'),
            apiRequest('/api/service-packages'),
            // Needed by every caller, not just the internal grid: this is the
            // price table for license SKUs, so the cart cannot price one without it.
            apiRequest('/api/workspaces'),
            apiRequest('/api/email-templates')
        ]).then(([customersData, catalogData, packagesData, workspacesData, templatesData]) => {
            const sortFunc = (a, b) => {
                const orderA = a.order !== undefined && a.order !== null && a.order !== '' ? Number(a.order) : Infinity;
                const orderB = b.order !== undefined && b.order !== null && b.order !== '' ? Number(b.order) : Infinity;
                return orderA - orderB;
            };
            const extraProducts = [...catalogData, ...packagesData].sort(sortFunc);
            workspacesData.sort(sortFunc);

            dispatch({ type: 'SET_CUSTOMERS', payload: customersData });
            dispatch({ type: 'SET_EXTRA_PRODUCTS', payload: extraProducts });
            dispatch({ type: 'SET_WORKSPACE_PRODUCTS', payload: workspacesData });
            dispatch({ type: 'SET_EMAIL_TEMPLATES', payload: templatesData });
            selectInitialCustomer(customersData);

            if (!canSelectAnyCustomer && customersData.length > 0) {
                const cust = customersData[0];
                if (cust.language && onLanguageChange) {
                    onLanguageChange(cust.language);
                }
            }

        }).catch(error => {
            console.error("Failed to fetch initial data:", error);
        }).finally(() => {
            dispatch({ type: 'SET_IS_LOADING', payload: false });
        });
    }, [authUser, getCustomersApiEndpoint, apiRequest, selectInitialCustomer, onLanguageChange,
        canSelectAnyCustomer]);

    // Effect for fetching detailed subscriptions when a customer is selected
    useEffect(() => {
        if (!selectedCustomer?.customerId || !googleIdToken) {
            dispatch({ type: 'SET_DETAILED_SUBSCRIPTIONS', payload: [] });
            return;
        }

        dispatch({ type: 'SET_IS_LOADING_DETAILS', payload: true });
        apiRequest(`/api/customers/${selectedCustomer.customerId}/licenses`)
            .then((details) => {
                dispatch({ type: 'SET_DETAILED_SUBSCRIPTIONS', payload: details });
            })
            .catch((err) => console.error("Failed to fetch subscription details:", err))
            .finally(() => dispatch({ type: 'SET_IS_LOADING_DETAILS', payload: false }));
    }, [selectedCustomer?.customerId, googleIdToken, apiRequest]);

    // Effect for syncing selectedCustomer to localStorage (for OrderHistory page)
    useEffect(() => {
        if (selectedCustomer) {
            localStorage.setItem('selectedCustomer', JSON.stringify(selectedCustomer));
        }
    }, [selectedCustomer]);

    const handleCustomerSave = async (customerId, updates) => {
        // Minimal payload: partial update avoids persisting read-time computed fields.
        await apiRequest(`/api/customers/${customerId}`,
            {
                method: 'POST',
                body: JSON.stringify(updates)
            }
        );
        setIsCustomerChanged(false);
        // Merge locally so read-time computed fields (billingDebtorNr, isActive default) survive.
        dispatch({ type: 'SAVE_CUSTOMER', payload: { ...selectedCustomer, ...updates } });
        dispatch({ type: 'SET_CUSTOMERS', payload: customers.map(c => c.customerId === customerId ? { ...c, ...updates } : c) });

        // Invalidate sessionStorage cache for initial data to ensure freshness
        try {
            sessionStorage.removeItem('admin_customers_cache');
            Object.keys(sessionStorage).forEach(key => {
                if (key.startsWith('dashboard_initial_data_')) {
                    sessionStorage.removeItem(key);
                }
            });
        } catch (e) {
            console.error("Failed to clear cached dashboard data:", e);
        }
    };

    // --- SHOPPING CART LOGIC ---
    const handleAddToCart = async (itemToAdd) => {
        dispatch({ type: 'ADD_TO_CART', payload: itemToAdd, t });
    };

    const handleRemoveFromCart = (itemId) => {
        dispatch({ type: 'REMOVE_FROM_CART', payload: itemId });
    };

    const handleRenewAll = async (subscriptionsToRenew) => {
        const eligibleSubs = subscriptionsToRenew.filter(sub => sub.renewalDate && sub.planName && sub.planName.includes("ANNUAL"));
        if (eligibleSubs.length === 0) {
            toast.error(t.no_renewable_licenses || "No renewable licenses found.");
            return;
        }

        setIsPlacingOrder(true);
        toast.loading(t.calculating_renewal_cost || "Calculating total renewal cost...", { id: "renewAll" });
        const itemsToAdd = [];

        const skipped = [];

        try {
            for (const sub of eligibleSubs) {
                let exactPrice = null;
                let afasArticleCode = "";
                if (exactPrice === null) {
                    let workspaceProduct = workspaces.find((p) => p.related_licenses_sku === sub.skuId) ||
                        extraProducts.find((p) => p.related_licenses_sku === sub.skuId);
                    if (workspaceProduct) {
                        exactPrice = workspaceProduct.price;
                        afasArticleCode = workspaceProduct.afas_artikelcode;
                    }
                }

                if (exactPrice !== null) {
                    const parsedPrice = parseFloat(exactPrice) || 0;
                    itemsToAdd.push({
                        id: `${sub.subscriptionId}-renewal`,
                        name: sub.skuName,
                        quantity: sub.assignedLicenses !== undefined && sub.assignedLicenses !== null ? sub.assignedLicenses : sub.purchasedLicenses,
                        type: 'license',
                        planName: sub.planName,
                        renewalDate: sub.renewalDate,
                        skuId: sub.skuId,
                        isRenewal: true,
                        fetchedYearlyPrice: parsedPrice,
                        price: parsedPrice,
                        yearlyPrice: parsedPrice,
                        afasArticleCode: afasArticleCode
                    });
                } else {
                    skipped.push(sub.skuName);
                }
            }

            toast.dismiss("renewAll");

            if (itemsToAdd.length === 0) {
                toast.error(t.no_prices_found || "Could not determine prices for any licenses.");
                setIsPlacingOrder(false);
                return;
            }

            console.log(itemsToAdd)

            itemsToAdd.forEach(item => {
                dispatch({ type: 'ADD_TO_CART', payload: item, t, silent: true });
            });
            toast.success(t.added_to_cart || "Licenses added to cart!");

            // A bulk renewal that quietly drops the SKUs it could not price would
            // have the customer paying for part of a renewal they think is whole.
            if (skipped.length > 0) {
                toast.error(
                    (t.renewal_items_skipped || "No price found for: {items}. These were not added.")
                        .replace('{items}', skipped.join(', '))
                );
            }
        } catch (error) {
            toast.dismiss("renewAll");
            console.error("Error during renew all:", error);
            toast.error(t.error_calculating_renewals || "Error calculating renewals.");
        } finally {
            setIsPlacingOrder(false);
        }
    };

    const refreshAppData = async () => {
        console.log("Order successful, refreshing data...");
        setPendingOrderId(null);
        setStoredPendingOrder(selectedCustomer?.customerId, null);
        dispatch({ type: 'RESET_CART' });
        const customersAPI = getCustomersApiEndpoint();
        const customersData = await apiRequest(customersAPI);
        dispatch({ type: 'SET_CUSTOMERS', payload: customersData });
        const updatedSelectedCustomer = customersData.find(
            c => c.customerId === selectedCustomer.customerId
        );
        if (updatedSelectedCustomer) {
            dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: updatedSelectedCustomer });
        }
    };

    const handlePlaceOrder = async (event, stripe, elements) => {
        event.preventDefault();

        if (!selectedCustomer || cartItems.length === 0) {
            toast.error(t.cannot_place_order_alert);
            return;
        }

        const isPostpaid = selectedCustomer?.isPostpaid || false;

        if (!isPostpaid && (!stripe || !elements)) {
            console.error("Stripe.js has not loaded yet.");
            setOrderConfirmation({ success: false, message: "Payment system is not ready. Please try again." });
            return;
        }

        setOrderConfirmation(null);
        setIsPlacingOrder(true);

        try {
            if (!isPostpaid) {
                const { error: submitError } = await elements.submit();
                if (submitError) {
                    setOrderConfirmation({ success: false, message: submitError.message });
                    setIsPlacingOrder(false);
                    return;
                }
            }

            const activeOrderId = pendingOrderId || getStoredPendingOrder(selectedCustomer?.customerId);

            const orderData = {
                customerId: selectedCustomer.customerId,
                orderUser: authUser.email,
                cartItems: cartItems,
                paymentMethod: isPostpaid ? 'postpaid' : 'stripe',
                paypalOrderId: null,
                purchaseOrder: purchaseOrder,
                existingOrderId: activeOrderId || undefined
            };

            const result = await apiRequest('/api/orders', {
                method: 'POST',
                body: JSON.stringify(orderData),
            });

            if (result.orderId) {
                setPendingOrderId(result.orderId);
                setStoredPendingOrder(selectedCustomer?.customerId, result.orderId);
            }

            if (isPostpaid) {
                setPendingOrderId(null);
                setStoredPendingOrder(selectedCustomer?.customerId, null);
                const successMsg = `${t.order_placed_success_message || 'Order successfully placed! Order ID:'} ${result.orderId}`;
                setOrderConfirmation({ success: true, message: successMsg, orderId: result.orderId });
                await wait(3000);
                await refreshAppData();
                return;
            }

            const clientSecret = result.clientSecret;
            if (!clientSecret) {
                throw new Error("Failed to get payment secret from server.");
            }

            const { error } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.origin + '/dashboard',
                    payment_method_data: {
                        billing_details: {
                            name: selectedCustomer.name,
                        },
                    },
                },
                redirect: 'if_required'
            });

            if (error) {
                throw new Error(error.message);
            }

            // Payment succeeded without redirect
            setPendingOrderId(null);
            setStoredPendingOrder(selectedCustomer?.customerId, null);
            const isAutoInvoice = selectedCustomer?.isAutoInvoiceEnabled !== false;
            const successMsg = isAutoInvoice
                ? (t.payment_success_invoice_email || "Payment successful. The invoice will be sent to your email.")
                : (t.payment_success_processed || "Payment successful. Your order has been processed.");
            setOrderConfirmation({ success: true, message: successMsg, orderId: result.orderId });
            await wait(3000);
            await refreshAppData();

        } catch (error) {
            console.error("Failed to place order:", error);
            const errorKey = error.data?.error_key;
            const translatedMsg = errorKey && t[errorKey];
            const errorMsg = translatedMsg || error.message || t.order_placed_error || 'An unexpected error occurred.';
            setOrderConfirmation({ success: false, message: errorMsg });
        } finally {
            setIsPlacingOrder(false);
        }
    };

    const handlePayPalSuccess = async (paypalDetails) => {
        setOrderConfirmation(null);
        setIsPlacingOrder(true);

        try {
            const capturedAmount = paypalDetails?.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value;
            const activeOrderId = pendingOrderId || getStoredPendingOrder(selectedCustomer?.customerId);

            const orderData = {
                customerId: selectedCustomer.customerId,
                orderUser: authUser.email,
                cartItems: cartItems,
                paymentMethod: 'paypal',
                paypalOrderId: paypalDetails.id,
                paypalCapturedAmount: capturedAmount ? parseFloat(capturedAmount) : null,
                purchaseOrder: purchaseOrder,
                existingOrderId: activeOrderId || undefined
            };

            const result = await apiRequest('/api/orders', {
                method: 'POST',
                body: JSON.stringify(orderData),
            });

            setPendingOrderId(null);
            setStoredPendingOrder(selectedCustomer?.customerId, null);
            const successMsg = `${t.order_placed_success_message || 'Bestelling succesvol geplaatst! Order ID:'} ${result.orderId}`;
            setOrderConfirmation({ success: true, message: successMsg, orderId: result.orderId });

            // Add a 3-second delay to allow payment gateway to update
            await wait(3000);
            await refreshAppData();

        } catch (error) {
            console.error("Failed to save PayPal order:", error);
            const errorKey = error.data?.error_key;
            const translatedMsg = errorKey && t[errorKey];
            const errorMsg = translatedMsg || error.message || "PayPal payment successful, but failed to save order.";
            setOrderConfirmation({ success: false, message: errorMsg });
        } finally {
            setIsPlacingOrder(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Joyride
                key={runTour ? 'tour-active' : 'tour-inactive'}
                steps={tourSteps}
                run={runTour}
                continuous
                showSkipButton
                showProgress
                callback={handleJoyrideCallback}
                locale={{
                    back: t.joyride_back || 'Back',
                    close: t.joyride_close || 'Close',
                    last: t.joyride_last || 'Last',
                    next: t.joyride_next || 'Next',
                    skip: t.joyride_skip || 'Skip'
                }}
                styles={{
                    options: {
                        primaryColor: '#831b84',
                    },
                    buttonNext: {
                        backgroundColor: '#831b84',
                    },
                    buttonBack: {
                        color: '#831b84',
                    }
                }}
            />
            <div className="lg:col-span-2">
                {canSelectAnyCustomer && (
                    <div className="mb-4 bg-white p-4 rounded-lg flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-gray-800 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <label htmlFor="customer-select" className="mr-2 font-semibold text-gray-700">{t.choose_customer}:</label>
                        <div className="flex-1 max-w-md tour-customer-selector">
                            <SearchableSelect
                                options={customerOptions}
                                value={selectedCustomer?.customerId || selectedCustomer?.id || ''}
                                onChange={(value) => {
                                    const cust = customers.find(c => (c.customerId || c.id) === value);
                                    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: cust });
                                    dispatch({ type: 'RESET_CART' });
                                }}
                                placeholder={t.choose_customer || "Choose customer..."}
                            />
                        </div>
                    </div>
                )}
                {isLoading ? (
                    <DashboardSkeleton isAdmin={canSelectAnyCustomer} />
                ) : selectedCustomer ? (
                    <>
                        {/* No isAdmin prop: CustomerSummary reads the two
                            capabilities it needs from context, because "may see
                            the status" and "may change it" are different
                            questions and one boolean answered both wrongly. */}
                        <CustomerSummary
                            customer={{
                                ...selectedCustomer,
                                customerDomain: selectedCustomer.customerDomain || selectedCustomer.customerName || selectedCustomer.name || selectedCustomer.id || ''
                            }}
                            t={t}
                            onCustomerSave={handleCustomerSave}
                            isCustomerChanged={isCustomerChanged}
                            setIsCustomerChanged={setIsCustomerChanged}
                            googleIdToken={googleIdToken}
                        />

                        {isLoadingDetails ? (
                            <SubscriptionsTableSkeleton t={t} />
                        ) : detailedSubscriptions.length > 0 ? (
                            <SubscriptionsTable
                                subscriptions={detailedSubscriptions}
                                onAddToCart={handleAddToCart}
                                onRenewAll={handleRenewAll}
                                emailTemplates={emailTemplates}
                                language={language}
                                t={t}
                                workspaces={workspaces}
                                extraProducts={extraProducts}
                            />
                        ) : (
                            <div className="bg-white p-12 rounded-lg mb-8 text-center shadow-sm border border-gray-100 italic text-gray-400 font-medium">
                                {t.no_subscriptions_found || "Geen actieve licenties gevonden voor deze klant."}
                            </div>
                        )}
                    </>
                ) : (
                    <p>{t.no_data_found}</p>
                )}
            </div>
            {/* Read-only holds no `purchase` capability, so the cart and
                checkout do not render at all rather than sitting there disabled.
                The main column below widens to fill the space. */}
            {canPurchase && (
            <div className="lg:col-span-1 tour-cart">
                {orderConfirmation && orderConfirmation.message && (
                    <div className={`p-4 mb-4 rounded-lg text-sm ${orderConfirmation.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} role="alert">
                        <p className="font-bold">{orderConfirmation.success ? (t.success_title || 'Succes') : (t.error_title || 'Fout')}</p>
                        <p>
                            {orderConfirmation.message}
                        </p>
                    </div>
                )}
                {getStripePromise(selectedCustomer?.administratie) ? (
                    <Elements
                        key={selectedCustomer?.administratie || 'default'}
                        stripe={getStripePromise(selectedCustomer?.administratie)}
                        options={{
                            mode: 'payment',
                            amount: Math.max(Math.round(paymentAmount * 100), 50), // At least 50 cents
                            currency: 'eur',
                        }}
                    >
                        <CheckoutFormContent
                            handlePlaceOrder={handlePlaceOrder}
                            cartItems={cartItems}
                            customer={selectedCustomer}
                            onRemoveFromCart={handleRemoveFromCart}
                            isPlacingOrder={isPlacingOrder}
                            onPayPalSuccess={handlePayPalSuccess}
                            purchaseOrder={purchaseOrder}
                            setPurchaseOrder={setPurchaseOrder}
                            t={t}
                            language={language}
                            setPaymentAmount={setPaymentAmount}
                        />
                    </Elements>
                ) : (
                    <div className="bg-white p-6 rounded-lg text-red-600">
                        {t.payment_system_not_ready || "Payment system is not ready. Please try again later."}
                    </div>
                )}
            </div>
            )}
            {!isLoading && extraProducts.length > 0 && (
                <div className="lg:col-span-3 -mt-8 tour-extra-products">
                    <ProductGridHorizontal products={extraProducts.filter(p => p.visible !== false)} onAddToCart={handleAddToCart} t={t} selectedCustomer={selectedCustomer} />
                </div>
            )}
            {selectedCustomer && canViewInternalProducts && (
                <div className="lg:col-span-3 mt-12">
                    <WorkspaceGrid workspaces={workspaces} subscriptions={detailedSubscriptions} onAddToCart={handleAddToCart} t={t} />
                </div>
            )}
        </div>
    );
};

const DashboardSkeleton = ({ isAdmin }) => {
    return (
        <div className="space-y-6 animate-pulse">
            {/* CustomerSummary Skeleton */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col space-y-4">
                <div className="flex justify-between items-center mb-2">
                    <div className="h-6 bg-gray-200 rounded w-48"></div>
                    <div className="h-5 bg-gray-200 rounded-full w-24"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="rounded-xl p-4 border bg-gray-50 border-gray-100 h-24">
                        <div className="h-3 bg-gray-200 rounded w-24 mb-3"></div>
                        <div className="h-5 bg-gray-200 rounded w-48"></div>
                    </div>
                    <div className="rounded-xl p-4 border bg-gray-50 border-gray-100 h-24">
                        <div className="h-3 bg-gray-200 rounded w-36 mb-3"></div>
                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                    </div>
                </div>
            </div>

            {/* Table Header/Rows Skeleton */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th className="px-6 py-3 text-left bg-purple-900/10">
                                <div className="h-3 bg-gray-200 rounded w-16"></div>
                            </th>
                            <th className="px-6 py-3 text-left bg-purple-900/10">
                                <div className="h-3 bg-gray-200 rounded w-8"></div>
                            </th>
                            <th className="px-6 py-3 text-left bg-purple-900/10">
                                <div className="h-3 bg-gray-200 rounded w-12"></div>
                            </th>
                            <th className="px-6 py-3 text-left bg-purple-900/10">
                                <div className="h-3 bg-gray-200 rounded w-20"></div>
                            </th>
                            <th className="px-6 py-3 text-left bg-purple-900/10">
                                <div className="h-3 bg-gray-200 rounded w-24"></div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {[1, 2].map((idx) => (
                            <tr key={idx}>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    <div className="flex items-center gap-4">
                                        <div className="h-8 bg-gray-200 rounded w-20"></div>
                                        <div className="h-8 w-8 bg-gray-100 rounded-full"></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* WorkspaceGrid Titles/Cards Skeleton */}
            {isAdmin && (
                <div className="space-y-4 pt-4">
                    <div className="h-7 bg-gray-200 rounded w-56 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((idx) => (
                            <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-56 p-6 justify-between">
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                                        <div className="h-5 bg-gray-200 rounded w-40"></div>
                                    </div>
                                    <div className="h-3.5 bg-gray-100 rounded w-24"></div>
                                </div>
                                <div className="h-14 bg-gray-50 rounded-xl border border-gray-100"></div>
                                <div className="flex justify-between items-center gap-4 pt-2">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                    <div className="h-9 bg-gray-200 rounded-lg w-28"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
